import { memo, useCallback, useMemo, useState } from 'react';
import { useEntryDescription } from '../../components/CreditLedger/LedgerRow';
import { useFormatDate } from '../../hooks/useFormatDate';
import { T, useT } from '../../translation/src';
import { InvoiceTrackingStatus, SuperadminInvoicesQuery, useSuperadminInvoiceCountsQuery, useSuperadminInvoicesQuery } from '../../types/graphqlTypes';
import { formatPrice } from '../../utils/formatPrice';
import { AdminListPage, AdminListTab, StatusPill, StatusTone } from './AdminListPage';

const PAGE_SIZE = 50;

type Tab = InvoiceTrackingStatus | 'ALL';
type InvoiceRow = SuperadminInvoicesQuery['superadminInvoices']['data'][number];

const STATUS_TONE: Record<InvoiceTrackingStatus, StatusTone> = {
  [InvoiceTrackingStatus.NoInvoice]: 'grey',
  [InvoiceTrackingStatus.Draft]: 'orange',
  [InvoiceTrackingStatus.Unpaid]: 'red',
  [InvoiceTrackingStatus.Paid]: 'green',
  [InvoiceTrackingStatus.Cancelled]: 'grey',
};

const useStatusLabels = (): Record<InvoiceTrackingStatus, string> => {
  const noInvoice = useT('no invoice', { swc: true });
  const draft = useT('draft', { swc: true });
  const unpaid = useT('unpaid', { swc: true });
  const paid = useT('paid', { swc: true });
  const cancelled = useT('cancelled', { swc: true });
  return {
    [InvoiceTrackingStatus.NoInvoice]: noInvoice,
    [InvoiceTrackingStatus.Draft]: draft,
    [InvoiceTrackingStatus.Unpaid]: unpaid,
    [InvoiceTrackingStatus.Paid]: paid,
    [InvoiceTrackingStatus.Cancelled]: cancelled,
  };
};

// One sale: a component of its own, so the ledger's own wording of the row (purchase, plan subscription,
// upgrade...) can be used.
const InvoiceTableRow_: React.FC<{ row: InvoiceRow; statusLabel: string }> = ({ row, statusLabel }) => {
  const { formatDate } = useFormatDate();
  const description = useEntryDescription(row.entry);
  const { entry } = row;
  const invoiceLabel = entry.odooInvoiceNumber ?? (entry.odooInvoiceId ? `#${entry.odooInvoiceId}` : null);

  return (
    <tr className="border-b border-gray-200 dark:border-gray-700 align-top">
      <td className="py-2 pr-4">
        <StatusPill tone={STATUS_TONE[row.status]}>{statusLabel}</StatusPill>
      </td>
      <td className="py-2 pr-4 whitespace-nowrap">{formatDate(entry.createdAt)}</td>
      <td className="py-2 pr-4">{row.organisation?.name ?? '-'}</td>
      <td className="py-2 pr-4">
        {description}
        {entry.amount > 0 && <span className="text-gray-500"> · {entry.amount}</span>}
      </td>
      <td className="py-2 pr-4 whitespace-nowrap font-mono">{formatPrice(entry.totalPriceCents ?? 0, entry.currency ?? undefined)}</td>
      <td className="py-2 pr-4 whitespace-nowrap">
        {entry.odooInvoiceUrl && invoiceLabel ? (
          <a href={entry.odooInvoiceUrl} target="_blank" rel="noreferrer" className="underline hover:text-neon-green-700 dark:hover:text-neon-green-300">
            {invoiceLabel}
          </a>
        ) : (
          (invoiceLabel ?? '-')
        )}
      </td>
      <td className="py-2 pr-4 text-gray-500">{entry.grantedByAccount ? `${entry.grantedByAccount.name ?? 'Pointorama'}${entry.grantedByAccount.isSuperAdmin ? ' (admin)' : ''}` : '-'}</td>
    </tr>
  );
};
const InvoiceTableRow = memo(InvoiceTableRow_);

// Superadmins: every sale to invoice across the organisations, and where its invoice stands in Odoo — a
// draft still to send (orange), sent and unpaid (red), paid (green). Replaces the purchase notifications.
const Invoices_: React.FC = () => {
  const [tab, setTab] = useState<Tab>(InvoiceTrackingStatus.Draft);
  const statusLabels = useStatusLabels();

  const status = tab === 'ALL' ? undefined : tab;
  const { data, loading, fetchMore } = useSuperadminInvoicesQuery({ variables: { status, pagination: { limit: PAGE_SIZE } }, fetchPolicy: 'cache-and-network' });
  const { data: countsData } = useSuperadminInvoiceCountsQuery({ fetchPolicy: 'cache-and-network' });
  const counts = countsData?.superadminInvoiceCounts;

  const page = data?.superadminInvoices;
  const rows = page?.data ?? [];
  const nextCursor = page?.nextCursor;
  const onLoadMore = useCallback(() => {
    if (!nextCursor) return;
    void fetchMore({
      variables: { status, pagination: { limit: PAGE_SIZE, cursor: nextCursor } },
      updateQuery: (previous, { fetchMoreResult }) => ({
        superadminInvoices: { ...fetchMoreResult.superadminInvoices, data: [...previous.superadminInvoices.data, ...fetchMoreResult.superadminInvoices.data] },
      }),
    });
  }, [fetchMore, nextCursor, status]);

  const tabs = useMemo<AdminListTab<Tab>[]>(
    () => [
      { key: InvoiceTrackingStatus.Draft, label: statusLabels[InvoiceTrackingStatus.Draft], count: counts?.draft, tone: 'orange' },
      { key: InvoiceTrackingStatus.Unpaid, label: statusLabels[InvoiceTrackingStatus.Unpaid], count: counts?.unpaid, tone: 'red' },
      { key: InvoiceTrackingStatus.Paid, label: statusLabels[InvoiceTrackingStatus.Paid], count: counts?.paid, tone: 'green' },
      { key: InvoiceTrackingStatus.NoInvoice, label: statusLabels[InvoiceTrackingStatus.NoInvoice], count: counts?.noInvoice, tone: 'grey' },
      { key: InvoiceTrackingStatus.Cancelled, label: statusLabels[InvoiceTrackingStatus.Cancelled], count: counts?.cancelled, tone: 'grey' },
      { key: 'ALL', label: <T _str="all" swc />, count: counts ? counts.draft + counts.unpaid + counts.paid + counts.noInvoice + counts.cancelled : undefined },
    ],
    [counts, statusLabels],
  );

  return (
    <AdminListPage
      title={<T _str="invoices" swc />}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      columns={[<T _str="status" swc />, <T _str="date" swc />, <T _str="organisation" swc />, <T _str="sale" swc />, <T _str="amount" swc />, <T _str="invoice" swc />, <T _str="by" swc />]}
      isEmpty={rows.length === 0}
      emptyText={<T _str="nothing here" swc />}
      loading={loading}
      hasMore={!!page?.hasMore}
      onLoadMore={onLoadMore}
      rows={rows.map((row) => (
        <InvoiceTableRow key={row.entry.id} row={row} statusLabel={statusLabels[row.status]} />
      ))}
    />
  );
};

export const Invoices = memo(Invoices_);
